import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from "@lexical/list";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $setBlocksType } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { Components } from "@thisgorilla/primate-ds";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  TextFormatType,
} from "lexical";
import { useCallback, useContext, useEffect, useState } from "react";

import styled from "styled-components";
import { ArticleContext } from "../..";
import { useAppSelector } from "../../../../../redux";
import { FlexRow } from "../../../../Common/Styles";
import StyleDropdown from "./styleDropdown";
import ToolbarForEditorProps from "./ToolbarForEditor";
import { formattingOptions } from "./utils";

const ToolbarForEditor = () => {
  const { editors, focusedEditor } = useContext(ArticleContext);
  const editor = editors[focusedEditor];
  const [activeFormats, setActiveFormats] = useState<
    Set<ToolbarForEditorProps.TextFormatType>
  >(new Set());
  const { isArticleFullScreen } = useAppSelector((state) => state.common);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const formats = new Set<ToolbarForEditorProps.TextFormatType>();

      if (selection.hasFormat("bold")) formats.add("bold");
      if (selection.hasFormat("italic")) formats.add("italic");
      if (selection.hasFormat("underline")) formats.add("underline");
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElement();

      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(
          anchorNode,
          ListNode
        );
        const type = parentList
          ? parentList.getListType()
          : element.getListType();
        if (type === "bullet") formats.add("ul");
        if (type === "number") formats.add("ol");
      }
      setActiveFormats(formats);
    }
  }, []);

  useEffect(() => {
    if (!editor) return;
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event: KeyboardEvent) => {
          event.preventDefault();
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;
          const anchorNode = selection.anchor.getNode();
          let currentNode = anchorNode.getParent();
          let depth = 0;
          while (currentNode !== null) {
            if ($isListNode(currentNode)) {
              depth++;
            }
            currentNode = currentNode.getParent();
          }
          if (event.shiftKey) {
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          } else if (depth < 3) {
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }
          return true;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateToolbar]);

  const formatText = (format: TextFormatType) => {
    if (!editor) return;
    editor.focus();
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatList = (format: "ol" | "ul") => {
    if (!editor) return;
    editor.focus();
    if (activeFormats.has(format)) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    } else {
      editor.update(() => {
        const root = $getRoot();
        if (root.getChildrenSize() === 0) {
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(""));
          root.append(paragraph);
          paragraph.select();
        }

        editor.dispatchCommand(
          format === "ol"
            ? INSERT_ORDERED_LIST_COMMAND
            : INSERT_UNORDERED_LIST_COMMAND,
          undefined
        );
      });
    }
  };

  return (
    <LeftContainer id="article-toolbar">
      <StyleDropdown />
      <FlexRow $gapInPx={8}>
        {formattingOptions.map((option, index) => (
          <ButtonWrapper
            key={index}
            $active={activeFormats.has(
              option.option as ToolbarForEditorProps.TextFormatType
            )}
          >
            <Components.Tooltip
              type="label"
              label={option.toolTipText}
              position={isArticleFullScreen ? "bottom" : "top"}
            >
              <Components.IconButton
                size="Small"
                kind="Tertiary"
                disabled={false}
                onClick={() =>
                  option.type === "text"
                    ? formatText(
                        option.option as ToolbarForEditorProps.OptionText
                      )
                    : option.type === "list"
                    ? formatList(
                        option.option as ToolbarForEditorProps.OptionList
                      )
                    : null
                }
                iconType={option.label}
              />
            </Components.Tooltip>
          </ButtonWrapper>
        ))}
        <Components.Tooltip
          type="label"
          label="Divider"
          position={isArticleFullScreen ? "bottom" : "top"}
        >
          <Components.IconButton
            size="Small"
            kind="Tertiary"
            disabled={false}
            onClick={() => {
              if (!editor) return;
              editor.focus();
              editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
            }}
            iconType={"Minus"}
          />
        </Components.Tooltip>
      </FlexRow>
    </LeftContainer>
  );
};

export default ToolbarForEditor;

const LeftContainer = styled(FlexRow)`
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  align-items: center;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary"].value};
  box-shadow: ${({ theme }) => theme.boxShadows["box-shadow-s"]};
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem
    ${({ theme }) => theme.radius["radius-none"].value}
    ${({ theme }) => theme.radius["radius-none"].value}
    ${({ theme }) => theme.radius["radius-xxs"].value}rem;
`;

const ButtonWrapper = styled.div<{ $active: boolean }>`
  & button {
    background: ${({ $active, theme }) =>
      $active &&
      theme.Color.elevation.surface["surface-primary-hover"].value} !important;
  }
`;
